import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import {
  BookkeeperUpdateIncidenceOptionSettingsDocument,
  BookkeepersCompanyIncidenceOptionsDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import companies from 'modules/core/store/collections/companies'
import app from 'modules/core/store/models/app'

import IncidenceOptionsSection from 'modules/bookkeepers/components/IncidenceOptionsSection'
import { useGetIncidenceSettings } from 'modules/bookkeepers/hooks/useGetIncidenceSettings'
import { useIncidenceFilterContext } from 'modules/bookkeepers/lib/IncidenceFilterContext'
import { PROFESSIONALS_APP } from 'modules/bookkeepers/lib/amplitudeData'
import type { IncidenceOptionsType } from 'modules/bookkeepers/lib/incidenceOptions'
import {
  createIncidenceOptionSettings,
  trackIncidenceOptionSettings,
} from 'modules/bookkeepers/lib/incidenceOptions'
import { INCIDENCE_TYPES } from 'modules/payroll_bundle/lib/Incidences/Types'

const ClientPreferences = () => {
  const { selectedCompany: filterContextCompany } = useIncidenceFilterContext()
  const selectedCompanyId = filterContextCompany?.attributes.id
  const {
    loading,
    employeeIncidenceOptions,
    defaultIncidenceOptions,
    leaveIncidenceOptions,
    companyIncidenceSettings,
  } = useGetIncidenceSettings(selectedCompanyId, true)

  const leaveIncidenceName = (leaveIdentifier: string) => {
    const nonCustomLeave = [INCIDENCE_TYPES.sick, INCIDENCE_TYPES.parental]

    if (nonCustomLeave.includes(leaveIdentifier)) return leaveIdentifier

    return INCIDENCE_TYPES.custom_leave
  }
  const [loadingIncidenceOption, setLoadingIncidenceOption] = React.useState<boolean>(false)

  const [updateIncidenceOptionSettings] = useMutation(
    BookkeeperUpdateIncidenceOptionSettingsDocument,
    {
      extractorFn: (data) =>
        data.bookkeepersManagementMutations.patchBookkeepersmanagementOptionIncidenceOption,
    }
  )

  const toggleIncidenceOption = async (
    id: number,
    trackIncidence: boolean,
    needsValidation: boolean
  ) => {
    const result = await updateIncidenceOptionSettings({
      context: {
        isBookkeeperView: true,
      },
      variables: {
        id: id,
        trackIncidence: trackIncidence,
        needsValidation: needsValidation,
      },
      refetchQueries: [BookkeepersCompanyIncidenceOptionsDocument],
    })

    const handled = handleResult(result, (_errors) => {})

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('app_store_v2.apps.payroll_bundle.settings.incidences.options.success'),
        time: 2000,
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('app_store_v2.apps.payroll_bundle.settings.incidences.options.error'),
        time: 2000,
      })
    }

    return handled
  }

  const incidenceOptionChange = async (type: IncidenceOptionsType, isValidation = false) => {
    const companyIncidenceOptionSettings = companyIncidenceSettings.find(
      (model) => model.incidenceName === type.id || model.leaveTypeId === Number(type.id)
    )
    const leave = leaveIncidenceOptions.find(({ id }) => id === type.id)
    const isLeaveIncidence = leave !== undefined
    const incidenceName = isLeaveIncidence ? leaveIncidenceName(leave.identifier) : type.id
    const company = companies.find({ id: selectedCompanyId })
    const isBookkeeperView = true
    setLoadingIncidenceOption(true)
    if (companyIncidenceOptionSettings?.id) {
      const tracking = isValidation
        ? companyIncidenceOptionSettings.trackIncidence
        : !companyIncidenceOptionSettings.trackIncidence
      const validating = isValidation
        ? !companyIncidenceOptionSettings.needsValidation
        : companyIncidenceOptionSettings.needsValidation
      toggleIncidenceOption(companyIncidenceOptionSettings.id, tracking, validating)
    } else {
      await createIncidenceOptionSettings(
        incidenceName,
        selectedCompanyId,
        isLeaveIncidence ? type.id : null,
        isBookkeeperView,
        isValidation
      )
    }
    setLoadingIncidenceOption(false)
    trackIncidenceOptionSettings(
      company?.attributes.name,
      incidenceName,
      isValidation,
      PROFESSIONALS_APP,
      companyIncidenceOptionSettings
    )
  }

  if (loading) return <Loading />

  return (
    <Box paddingX="s40">
      <IncidenceOptionsSection
        illustration="document-edit"
        title={i18n.t('bookkeepers.clients.preferences.section_titles.contract')}
        description={i18n.t('bookkeepers.clients.preferences.section_descriptions.contract')}
        options={defaultIncidenceOptions}
        onTogglerChange={incidenceOptionChange}
        companyIncidenceSettings={companyIncidenceSettings}
        onLoadingIncidenceOption={loadingIncidenceOption}
      />
      <IncidenceOptionsSection
        illustration="user"
        title={i18n.t('bookkeepers.clients.preferences.section_titles.personal')}
        description={i18n.t('bookkeepers.clients.preferences.section_descriptions.personal')}
        options={employeeIncidenceOptions}
        onTogglerChange={incidenceOptionChange}
        companyIncidenceSettings={companyIncidenceSettings}
        onLoadingIncidenceOption={loadingIncidenceOption}
      />
      <IncidenceOptionsSection
        illustration="calendar"
        title={i18n.t('bookkeepers.clients.preferences.section_titles.absences')}
        description={i18n.t('bookkeepers.clients.preferences.section_descriptions.absences')}
        options={leaveIncidenceOptions}
        onTogglerChange={incidenceOptionChange}
        companyIncidenceSettings={companyIncidenceSettings}
        onLoadingIncidenceOption={loadingIncidenceOption}
      />
    </Box>
  )
}

export default observer(ClientPreferences)
